import { bindEmailSend } from './email-send'
import { businessSettings, hasBusinessSettings } from './business-settings'
import { linkFor, roundMessageBy, wayFor, wayWords, MESSAGE_BY_OPTIONS, type MessageBy } from './contact'
import { wayIcon } from './contact-ui'
import { firstName } from './late-message'
import { el, telHref } from './ui'

// Contact a customer from where their visit is (Liam, 21 Oct 2026, after rearranging Lyndsey: "there was no easy way to
// text her or message her ... Unsure you and astra decide"; then, on a Message tile beside Call: "Five tiles is too
// crowded ... if we put call behind this here, we may want to in other parts of the site so the user gets used to that").
// Decided with Astra (docs/plans/contact-from-the-day/PLAN.md, reviews/astra-design.md and astra-r2.md):
//   - one "Contact" button where Call was; the whole button opens the choices, under it: Call and the number first, then
//     every way that reaches them, their usual one marked "Usual" (no hidden one-tap channel, no small corner target);
//   - a message way opens a chat with "Hi Lyndsey, " typed in. Roundly records nothing and their saved way never changes
//     (Messages' arrow does change it, on purpose: that is a setting, this is a choice for once);
//   - only Call reaches them (Don't message, or no other way): the plain Call button, as before;
//   - in a menu (the Diary's ⋯, a running job's More) the same choices as flat lines, no second arrow;
//   - recorded messages (Messages' Send, asking about a day, Owed's chases) stay their own buttons.
// Used on Today's cards, a running job's More, the Diary's visit details and ⋯, and Owed's "Booked today".

export interface ContactCustomer {
  name: string
  phone?: string | null
  email?: string | null
  message_by?: string | null
}

export interface ContactWay { way: MessageBy | 'call'; label: string; detail: string | null; href: string; usual: boolean }

// Every way that reaches them now: Call first, then their usual message way, the round's, and the rest. A way with no
// number or address that takes it is left out; Don't message leaves only Call.
export function contactWays(c: ContactCustomer): ContactWay[] {
  const out: ContactWay[] = []
  const tel = telHref(c.phone ?? null)
  if (tel) out.push({ way: 'call', label: 'Call', detail: (c.phone ?? '').trim(), href: tel, usual: false })
  const usual = wayFor(c)
  if (usual === 'none') return out
  const greeting = `Hi ${firstName(c.name) || c.name}, `
  const reach = { phone: c.phone ?? null, email: c.email ?? null }
  // An email's subject names the business, as the customer page's and Owed's did (Astra's final review: not every job
  // is windows).
  const subject = hasBusinessSettings() && businessSettings().business_name ? `From ${businessSettings().business_name}` : 'Hello'
  for (const way of new Set([usual, roundMessageBy(), ...MESSAGE_BY_OPTIONS.map((o) => o.value)])) {
    const href = linkFor(way, reach, greeting, subject).href
    if (href) out.push({ way, label: wayWords(way).short, detail: null, href, usual: way === usual })
  }
  return out
}

let dismissBound = false
const openPanels = new Set<(inside: Node | null) => void>()
function bindDismiss(): void {
  if (dismissBound) return
  dismissBound = true
  const closeAll = (inside: Node | null): void => { for (const close of [...openPanels]) close(inside) }
  // Before anything else sees the tap, so a menu that keeps its own taps to itself (the sheet's More) still closes them.
  document.addEventListener('click', (e) => closeAll(e.target as Node), true)
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeAll(null) })
  window.addEventListener('hashchange', () => closeAll(null))
}

const wayLink = (w: ContactWay, cls: string, words: Node[], name: string): HTMLAnchorElement => {
  // A web link (WhatsApp) opens beside Roundly; a phone's own call, text or email link opens its app. A call carries
  // their name for Roundly's own calling (calls.ts onPhoneLink), never the line's words ("Call 07700 900123").
  const a = el('a', { href: w.href, class: cls, ...(/^https?:/i.test(w.href) ? { target: '_blank', rel: 'noopener' } : {}), ...(w.way === 'call' ? { 'data-call-name': name } : {}) }) as HTMLAnchorElement
  if(w.way === 'email')bindEmailSend(a,()=>({href:w.href,name,confirm:false,sent:()=>{}}))
  a.append(...words)
  return a
}
const wayGlyph = (w: ContactWay): Node => w.way === 'call' ? el('span', { class: 'ic ic-phone contact-call-glyph', 'aria-hidden': 'true' }) : wayIcon(w.way)

// The Contact button and its choices, placed by the caller: the button where Call was, the choices right under the row
// it sits in (they open in place, never as a pop-over). With one way only, the button is that way (Call, as before) and
// there are no choices. Null when nothing reaches them. `cls` is the button's own look (Today's today-tool, the Diary
// details' btn-secondary, Owed's owed-call-btn); `onOpen` runs as a call or chat opens.
export function contactButton(c: ContactCustomer, cls: string, onOpen?: () => void): { button: HTMLElement; choices: HTMLElement | null } | null {
  const ways = contactWays(c)
  if (!ways.length) return null
  const who = firstName(c.name) || c.name
  if (ways.length === 1) {
    const only = ways[0]
    const a = only.way === 'call'
      ? wayLink(only, `${cls} call-link ic ic-phone`, [document.createTextNode('Call')], c.name)
      : wayLink(only, `${cls} contact-only`, [wayIcon(only.way), el('span', {}, only.label)], c.name)
    a.setAttribute('aria-label', only.way === 'call' ? `Call ${who}` : `Message ${who} by ${only.label}`)
    if (onOpen) a.addEventListener('click', onOpen)
    return { button: a, choices: null }
  }
  bindDismiss()
  const id = `contact-${Math.random().toString(36).slice(2, 9)}`
  const button = el('button', { type: 'button', class: `${cls} contact-open ic ic-contact`, 'aria-expanded': 'false', 'aria-controls': id },
    el('span', { class: 'contact-open-words' }, 'Contact', el('span', { class: 'way-menu-arrow', 'aria-hidden': 'true' })))
  button.setAttribute('aria-label', `Contact ${who}: call or message`)
  const choices = el('div', { class: 'contact-choices', id, role: 'group', 'aria-label': `Contact ${who}` })
  choices.hidden = true
  choices.append(el('div', { class: 'contact-choices-head' }, `Contact ${who}`))
  // A tap outside them, Escape, or another screen closes them; a tap on the button or inside them does not.
  const close = (inside: Node | null): void => {
    if (inside && (button.contains(inside) || choices.contains(inside))) return
    // Escape with a choice focused: the focus goes back to Contact, not into nothing (Astra's final review).
    if (!inside && choices.contains(document.activeElement)) button.focus()
    choices.hidden = true; button.setAttribute('aria-expanded', 'false'); button.classList.remove('contact-on'); openPanels.delete(close)
  }
  for (const w of ways) {
    const row = wayLink(w, `contact-choice contact-choice-${w.way}`, [wayGlyph(w), el('span', { class: 'contact-choice-words' }, w.label),
      ...(w.detail ? [el('span', { class: 'contact-choice-note' }, w.detail)] : w.usual ? [el('span', { class: 'contact-choice-note' }, 'Usual')] : [])], c.name)
    row.addEventListener('click', () => { close(null); onOpen?.() })
    choices.append(row)
  }
  // The tap goes on to the page, so other open menus close as usual; the choices' own closing ignores their button.
  button.addEventListener('click', () => {
    const opening = choices.hidden
    for (const other of [...openPanels]) other(null)
    if (!opening) return
    choices.hidden = false; button.setAttribute('aria-expanded', 'true'); button.classList.add('contact-on'); openPanels.add(close)
  })
  return { button, choices }
}

// The same choices as flat lines for a menu (the Diary's ⋯, a running job's More): Call when `call`, then the message
// ways ("WhatsApp Lyndsey", their usual one marked). `cls` is each line's own look; `icons` where the menu's lines have them.
export function contactLines(c: ContactCustomer, cls: string, opts: { call: boolean; icons?: boolean; onOpen?: () => void }): HTMLAnchorElement[] {
  const who = firstName(c.name) || c.name
  return contactWays(c).filter((w) => opts.call || w.way !== 'call').map((w) => {
    const words = document.createTextNode(w.way === 'call' ? `Call ${w.detail}` : `${w.label} ${who}`)
    const a = wayLink(w, `${cls} contact-line`, opts.icons ? [wayGlyph(w), words] : [words], c.name)
    if (w.usual) a.append(el('span', { class: 'row-menu-when' }, 'usual'))
    a.setAttribute('role', 'menuitem')
    if (opts.onOpen) a.addEventListener('click', opts.onOpen)
    return a
  })
}
